import { makeAutoObservable, runInAction } from 'mobx';
import i18n from 'i18n';
import { EMAIL_REGISTRATION_STEPS } from 'components/common/constants';
import { prepareFieldErrors } from 'services/utils';
import { getAllParamsFromLocalStorage } from 'components/utils';
import {
  beginEmailRegistration,
  regenerateEmailRegistration,
  verifyEmailRegistrationCode,
  verifyEmailRegistrationToken,
  completeEmailRegistration
} from 'services/requestAgent';

// Error codes that mean the whole registration attempt is dead and must restart from EMAIL — matches the
// terminal codes documented for /code/verify, /token/verify and /complete in docs/specs/TRN-6922_email_registration/
// spec.md §7.3 and the TRN-6966 API doc's per-endpoint error lists.
const CODE_VERIFY_RESTART_CODES = [
  'REGISTRATION_CODE_TRIES_EXCEEDED',
  'REGISTRATION_CODE_EXPIRED',
  'REGISTRATION_LINK_EXPIRED',
  'REGISTRATION_NO_LONGER_VALID',
  'NOT_FOUND'
];
const COMPLETE_RESTART_CODES = ['REGISTRATION_PASSWORD_SETUP_EXPIRED', 'REGISTRATION_NOT_VERIFIED', 'NOT_FOUND'];

// TRN-6922 — the new V2 email-registration flow (Email -> Confirm -> Password). A fresh store, not an
// extension of stores/registrationStore.js — the shapes don't overlap (responseId rotation, code-vs-token
// duality, a password-setup expiry window) and that legacy store is being deleted once this flow replaces it.
// See docs/specs/TRN-6922_email_registration/spec.md §7.
class EmailRegistrationStore {
  step = EMAIL_REGISTRATION_STEPS.EMAIL;
  email = '';
  responseId = null;
  deliveryType = null;
  isLoading = false;
  error = null;
  fieldErrors = null;
  regenerationCount = 0;
  lastRegeneratedAt = null;
  isRegistrationComplete = false;

  constructor() {
    makeAutoObservable(this);
  }

  setEmail(email) {
    this.email = email;
  }

  setResponseId(responseId) {
    this.responseId = responseId;
  }

  setStep(step) {
    this.step = step;
  }

  clearError() {
    this.error = null;
    this.fieldErrors = null;
  }

  setIsLoading(status) {
    this.isLoading = status;
    this.error = null;
    this.fieldErrors = null;
  }

  resetStore() {
    this.step = EMAIL_REGISTRATION_STEPS.EMAIL;
    this.email = '';
    this.responseId = null;
    this.deliveryType = null;
    this.isLoading = false;
    this.error = null;
    this.fieldErrors = null;
    this.regenerationCount = 0;
    this.lastRegeneratedAt = null;
    this.isRegistrationComplete = false;
  }

  // "Restart" branch shared by verifyCode/verifyToken/complete's terminal error codes — same as resetStore()
  // except it deliberately keeps the error/fieldErrors just set by the caller (resetStore() would wipe them
  // before the EMAIL step gets a chance to show the explanatory message).
  restartAfterFailure() {
    this.step = EMAIL_REGISTRATION_STEPS.EMAIL;
    this.email = '';
    this.responseId = null;
    this.deliveryType = null;
    this.regenerationCount = 0;
    this.lastRegeneratedAt = null;
  }

  // TRN-7489: "Change email" on CONFIRM — back to EMAIL with the address kept (EmailStep pre-fills it). The
  // started registration is just abandoned: the next begin() starts a new one with its own responseId.
  changeEmail() {
    const { email } = this;
    this.resetStore();
    this.email = email;
  }

  async begin(captcha) {
    this.setIsLoading(true);

    try {
      const { responseId, deliveryType } = await beginEmailRegistration(
        { email: this.email, utmTags: getAllParamsFromLocalStorage() },
        captcha
      );

      runInAction(() => {
        this.responseId = responseId;
        // Display-only — never branched on (anti-enumeration, spec §4): the already-registered branch omits
        // it, but the UI proceeds to CONFIRM identically either way.
        this.deliveryType = deliveryType;
        this.isLoading = false;
        this.step = EMAIL_REGISTRATION_STEPS.CONFIRM;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
        this.fieldErrors = prepareFieldErrors(i18n, err, undefined, true);
      });
    }
  }

  async verifyCode(code) {
    this.setIsLoading(true);

    try {
      const { responseId, deliveryType } = await verifyEmailRegistrationCode(this.responseId, code);

      runInAction(() => {
        this.responseId = responseId;
        this.deliveryType = deliveryType;
        this.isLoading = false;
        this.step = EMAIL_REGISTRATION_STEPS.PASSWORD;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        // REGISTRATION_CODE_NOT_VALID is retryable — stays on CONFIRM. Every other code here means the
        // registration itself is dead (tries exceeded / expired / rotated-away responseId) — restart.
        if (CODE_VERIFY_RESTART_CODES.includes(err?.code)) {
          this.restartAfterFailure();
        }
        this.error = err;
        this.fieldErrors = prepareFieldErrors(i18n, err, undefined, true);
      });
    }
  }

  // Magic-link deep-link path (components/Registration/Email/index.js, `/password-setup?responseId=..&token=..`).
  // Per the API doc, a wrong/expired token has no retry (unlike a wrong code) — any failure here restarts the
  // whole registration, not just the specific terminal codes verifyCode() checks for.
  async verifyToken(token) {
    this.setIsLoading(true);

    try {
      const { responseId, deliveryType } = await verifyEmailRegistrationToken(this.responseId, token);

      runInAction(() => {
        this.responseId = responseId;
        this.deliveryType = deliveryType;
        this.isLoading = false;
        this.step = EMAIL_REGISTRATION_STEPS.PASSWORD;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.restartAfterFailure();
        this.error = err;
      });
    }
  }

  async regenerate() {
    this.setIsLoading(true);

    try {
      const { responseId, deliveryType } = await regenerateEmailRegistration(this.responseId);

      runInAction(() => {
        this.responseId = responseId;
        this.deliveryType = deliveryType;
        this.regenerationCount += 1;
        this.lastRegeneratedAt = Date.now();
        this.isLoading = false;
      });
    } catch (err) {
      // REGISTRATION_REGENERATION_LIMIT_EXCEEDED / REGISTRATION_REGENERATION_COOLDOWN surface inline without
      // resetting the store — the user's still-valid current code/token keeps working.
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
    }
  }

  async complete(password) {
    this.setIsLoading(true);

    try {
      await completeEmailRegistration(this.responseId, password);

      runInAction(() => {
        this.isLoading = false;
        this.isRegistrationComplete = true;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        if (COMPLETE_RESTART_CODES.includes(err?.code)) {
          this.restartAfterFailure();
        }
        // ARGUMENT_NOT_VALID (weak password despite passing client-side checks) stays on PASSWORD with
        // fieldErrors set — same shape as every other flow's inline field errors.
        this.error = err;
        this.fieldErrors = prepareFieldErrors(i18n, err, undefined, true);
      });
    }
  }
}

export default new EmailRegistrationStore();
